/**
 * 영상 박스 백엔드(/videobox) 클라이언트 래퍼. 기존 코드와 독립 — 순수 fetch.
 * 서버 라우트: MoCombinator/src/videobox/routes.ts.
 * 시각은 epoch(ms) 로 다루고, 서버에는 ISO 문자열(startAt/endAt)로 전송한다.
 */

/** 항목 기간. end=null → 무한(open). (서버 resolve.ts Interval 을 평평하게 편 형태.) */
export interface BoxItemView {
  id: number;
  fileHash: string;
  createdAt: number; // epoch ms
  startAt: number;   // epoch ms
  endAt: number | null;
}

export interface VideoBox {
  id: number;
  /** 박스 소유 모피어(107) — 영상과 같은 축. 배치는 슬롯 쪽 위임 규칙이 별도 판정. */
  moPeerHash: string;
  name: string;
  defaultFileHash: string | null;
  boxType: 'schedule' | 'sequence' | 'group';
  seqCursor: number;
}

/** 083: 파일/하위박스 통합 항목 뷰(서버 listEntries). */
export interface EntryView {
  id: number;
  fileHash: string | null;
  childBoxId: number | null;
  childBoxName: string | null;
  childBoxType: 'schedule' | 'sequence' | 'group' | null;
  startAt: string;          // ISO
  endAt: string | null;     // ISO
  position: number | null;
}

export interface BoxDetail {
  box: VideoBox | undefined;
  defaultFileHash: string | null;
  items: BoxItemView[];
  entries: EntryView[];
}

/** 하이어라키 오버레이용: 어느 슬롯이 어느 박스로 구동되는지. */
export interface BoxBinding {
  set_name: string;
  slot_number: number;
  box_id: number;
  box_name: string;
  box_type: 'schedule' | 'sequence' | 'group';
}

// 서버 BoxItem: { id, fileHash, createdAt, interval: Bounded|OpenEnded }
interface RawInterval { kind: 'bounded' | 'open'; start: number; end?: number }
interface RawItem { id: number; fileHash: string; createdAt: number; interval: RawInterval }

const flattenItem = (it: RawItem): BoxItemView => ({
  id: it.id,
  fileHash: it.fileHash,
  createdAt: it.createdAt,
  startAt: it.interval.start,
  endAt: it.interval.kind === 'bounded' ? (it.interval.end ?? null) : null,
});

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/videobox${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error || `요청 실패 (${res.status})`);
  }
  return json.data as T;
}

// ── 박스 ─────────────────────────────────────────────────────────────
export const createBox = (name: string, boxType: 'schedule' | 'sequence' | 'group' = 'schedule') =>
  api<VideoBox>('/', { method: 'POST', body: JSON.stringify({ name, boxType }) });

/** 내 박스 전부 — 소유=peer(107) 라 family 무관. */
export const listBoxes = () => api<VideoBox[]>('/mine');

export async function getBox(boxId: number): Promise<BoxDetail> {
  const d = await api<{ box: VideoBox | undefined; defaultFileHash: string | null; items: RawItem[]; entries: EntryView[] }>(`/${boxId}`);
  return { box: d.box, defaultFileHash: d.defaultFileHash, items: d.items.map(flattenItem), entries: d.entries ?? [] };
}

export const patchBox = (boxId: number, body: { name?: string; defaultFileHash?: string | null; boxType?: 'schedule' | 'sequence' | 'group' }) =>
  api<void>(`/${boxId}`, { method: 'PATCH', body: JSON.stringify(body) });

export const deleteBox = (boxId: number) =>
  api<void>(`/${boxId}`, { method: 'DELETE' });

// ── 항목 ─────────────────────────────────────────────────────────────
/** @param startAt/endAt epoch ms. endAt=null → 무한. */
export const addItem = (boxId: number, fileHash: string, startAt: number, endAt: number | null) =>
  api<{ itemId: number }>(`/${boxId}/items`, {
    method: 'POST',
    body: JSON.stringify({
      fileHash,
      startAt: new Date(startAt).toISOString(),
      endAt: endAt === null ? null : new Date(endAt).toISOString(),
    }),
  });

/** 하위 박스 항목 추가(중첩). 서버가 사이클/한도/같은-family 를 거부. */
export const addChildBoxItem = (boxId: number, childBoxId: number, startAt: number, endAt: number | null) =>
  api<{ itemId: number }>(`/${boxId}/items`, {
    method: 'POST',
    body: JSON.stringify({
      childBoxId,
      startAt: new Date(startAt).toISOString(),
      endAt: endAt === null ? null : new Date(endAt).toISOString(),
    }),
  });

export const removeItem = (itemId: number) =>
  api<void>(`/items/${itemId}`, { method: 'DELETE' });

/** 항목 재생 기간 수정. @param startAt/endAt epoch ms. endAt=null → 무한. */
export const updateItem = (itemId: number, startAt: number, endAt: number | null) =>
  api<void>(`/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify({
      startAt: new Date(startAt).toISOString(),
      endAt: endAt === null ? null : new Date(endAt).toISOString(),
    }),
  });

// ── 슬롯 넣기/빼기 ───────────────────────────────────────────────────
/** 동률 묶기 — 같은 창 항목을 그룹으로(합류 또는 생성+이동). 서버 트랜잭션. */
export const tieGroup = (boxId: number, fileHash: string, startAt: number, endAt: number | null) =>
  api<{ groupId: number; groupName: string; created: boolean }>(`/${boxId}/tie-group`, {
    method: 'POST',
    body: JSON.stringify({ fileHash, startAt: new Date(startAt).toISOString(), endAt: endAt === null ? null : new Date(endAt).toISOString() }),
  });

/** 박스를 슬롯에 배치. setName 생략 시 서버가 그 family 의 **활성 셋**으로 해석한다
 *  (전역 편집 셋 이름을 남의 family 에 넘겨 팬텀 바인딩을 만들던 함정 차단). */
export const bindSlot = (boxId: number, moFamilyHash: string, slotNumber: number, setName?: string) =>
  api<{ setName: string }>(`/${boxId}/bind`, {
    method: 'POST',
    body: JSON.stringify({ moFamilyHash, slotNumber, ...(setName ? { setName } : {}) }),
  });

export const unbindSlot = (moFamilyHash: string, setName: string, slotNumber: number) =>
  api<void>('/unbind', { method: 'POST', body: JSON.stringify({ moFamilyHash, setName, slotNumber }) });

export const listBindings = (moFamilyHash: string) =>
  api<BoxBinding[]>(`/bindings/${moFamilyHash}`);

/**
 * 슬롯셋에서 **진짜 빈 슬롯** 번호를 하나 고른다(가장 빠른 번호). 없으면 null.
 *
 * ⚠️ "빈 슬롯"은 `file_hash` 가 없다는 것만으로 판정하면 안 된다. 박스가 슬롯을 차지한
 * 사실은 `video_box_slots` 바인딩으로만 표현되고, 슬롯의 file_hash 에는 **박스가 지금
 * 해석해 낸 영상**이 들어간다. 박스가 아무것도 못 내놓는 순간(항목 없음·스케줄 시간대
 * 밖·기본영상 없음) file_hash 가 null 이 되어 빈 슬롯과 구별되지 않는다.
 *
 * 그 슬롯에 영상을 넣으면 넣은 직후엔 정상으로 보이다가 박스 스케줄러가 다음 틱에
 * 덮어써서 **조용히 사라진다**. 2026-08-08 이전에는 박스 넣기 경로만 이 판정을 했고
 * 영상 넣기 경로는 file_hash 만 봐서 박스가 쓰는 슬롯을 골랐다 — 판정이 두 군데로
 * 갈라져 있던 게 원인이라 여기 한 곳으로 모은다. 서버도 같은 규칙으로 거부한다
 * (slots.ts 의 SLOT_BOUND_TO_BOX).
 */
export const findEmptySlotNumber = async (
  moFamilyHash: string,
  setName: string,
  slots: Array<{ slot_number: number; file_hash?: string | null }>,
): Promise<number | null> => {
  const bindings = await listBindings(moFamilyHash);
  const bound = new Set(
    bindings.filter((b) => b.set_name === setName).map((b) => b.slot_number),
  );
  const empty = slots
    .slice()
    .sort((a, b) => a.slot_number - b.slot_number)
    .find((s) => !s.file_hash && !bound.has(s.slot_number));
  return empty ? empty.slot_number : null;
};
